import {
  Map, Receipt, ReceiptText, BookOpen, User, Calendar,
  ChefHat, Archive, ChartColumn, ChartLine, Settings as SettingsIcon,
  ShieldCheck, BellRing, ShoppingCart, CookingPot, History, ListChecks,
  Baby, ConciergeBell, Calculator,
  // One glyph per new rail row: the fleet, menu governance, the wallet.
  Printer, GitBranch, Wallet,
  // ADR-0023 §19 — franchise and brand governance. `Building2` is unused
  // elsewhere on the rail and is literally what a brand groups: branches.
  Building2,
  // §2 / §3 — the integration substrate and outbound webhooks. `Webhook` is
  // unused elsewhere on the rail and is literally what half this row is.
  Webhook,
  // ADR-0028 Part B — delivery aggregators. `ShoppingBag` reads as a
  // marketplace order rather than a physical delivery vehicle (`Truck`,
  // Central Kitchen's own glyph, two rows down) or in-venue commerce
  // (`ShoppingCart`, Procurement's), and is unused elsewhere on the rail.
  ShoppingBag,
  // §5 — Egypt fiscal compliance: a building that reports to an authority.
  // §18 — the commissary's own transport, the one glyph on the rail that is
  // literally a delivery vehicle.
  Landmark, Truck,
  // ADR-0028 Part A — the accounting posting ledger. Debits and credits
  // balance, which is literally what `Scale` draws; unused elsewhere on the
  // rail and deliberately not `Landmark` (Fiscal's own glyph, two rows up) —
  // a private ledger is not a government authority, and the two screens
  // being architectural siblings (both fan out through the same integration
  // substrate, per ADR-0028's own framing) is exactly why they must not also
  // share a glyph nobody could tell apart collapsed.
  Scale,
  // §22 / ADR-0017 — anomaly detection. `ShieldAlert` and not `ShieldCheck`
  // (Approvals' own glyph, two rows up): a signed approval is a settled
  // decision, a risk case is an open one.
  ShieldAlert,
  // §4 / ADR-0008 — the time clock and the workforce management screen.
  // `Clock` is unused elsewhere and is literally what the station is; `Users`
  // (plural) is distinct from `User` (singular, Customers' own glyph two rows
  // up in the source order) — same reasoning that keeps a merged duplicate
  // and a live customer from sharing a name.
  Clock, Users,
  // ADR-0032 — the governed operations copilot. `Sparkles` is unused
  // elsewhere on the rail and is the one row that answers a typed question
  // rather than showing a list; forecasting (ADR-0031) is a Reports tab, not
  // a row of its own, so this is the only new glyph this pass adds.
  Sparkles,
  // ADR-0016 §8 — the branded storefront console. Unused elsewhere on the rail.
  Globe,
  // ADR-0026 §9 — templates, campaigns and quiet hours. Unused elsewhere.
  MessageSquare,
  // ADR-0021 — the call-centre order desk. `Headset` is unused elsewhere and
  // is literally the hardware an agent wears; distinct from `BellRing`
  // (Service requests' glyph, a floor call) and `ConciergeBell` (the waiter
  // pass).
  Headset,
  // §16 / ADR-0029 — own delivery & driver operations. `Bike` is unused
  // elsewhere and is literally one of the vehicles a `Driver` row can carry
  // (`DriverVehicleType.BICYCLE`); `Truck` (Central Kitchen's own glyph, five
  // rows down) is the commissary's supply run between branches, a different
  // thing moving for a different reason, so this deliberately does not reuse it.
  Bike,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { SETUP_FIX_PERMISSIONS } from '@/Pages/Setup/useSetupStatus';

export interface NavEntry {
  to: string;
  /** The label's i18n key. */
  key: string;
  icon: LucideIcon;
  /** Any one of these shows the row. Empty means everybody. */
  perms: string[];
  /**
   * A last-resort label, and deliberately unused by every entry below.
   *
   * `Layout` translates a *variable*, so `eslint-rules/i18n-key-exists` cannot
   * see these keys and a missing one draws the dotted key itself in the rail.
   * That is what this was for — but an English fallback in an options object is
   * exactly what the lint rule's own note calls out: it renders English to an
   * Arabic reader and makes a present key and an absent one look identical at
   * the call site. `nav.test.ts` asserts every key here is published in both
   * locale files instead, which is a guard a translator can actually clear.
   */
  defaultValue?: string;
}

/**
 * The rail, with the glyphs the Figma nav actually draws.
 *
 * Wave 1 proved the design's icons are lucide, so this is a mapping rather than
 * an asset import: Overview `chart-column`, Reports `chart-line` and
 * Reservations `calendar` are geometry-proven; the rest were read off a 16px
 * render and are named in the report as the judgement calls they are.
 *
 * No two entries may share a glyph. Collapsed, the rail is 64px of icons with
 * no labels at all, so a duplicate is two rows that cannot be told apart —
 * which is why Recipes moves off `book-open` when Catalog takes it, and why
 * Customers is a single `user`: it shared `users` with Parties, which is no
 * longer a row at all.
 */
export const NAV: NavEntry[] = [
  { to: '/', key: 'nav.overview', icon: ChartColumn, perms: [] },
  { to: '/floor-map', key: 'nav.floorMap', icon: Map, perms: ['floor_map.view'] },
  // §11 — a station screen, so it earns a row: the desk is opened once at the
  // start of a shift and left running, which a dialog on the floor map cannot
  // be. Same key as its route, `baby` because no other row is about children.
  // `children.view` and not `resource_sessions.view`: the board names children
  // and the adults who may collect them, and a waiter holds the timer key.
  { to: '/kids', key: 'nav.kidsArea', icon: Baby, perms: ['children.view'] },
  { to: '/orders', key: 'nav.orders', icon: Receipt, perms: ['orders.view'] },
  // ADR-0028 Part B — a marketplace order waits here for an explicit staff
  // Accept before it ever reaches a kitchen ticket, regardless of the
  // venue's own waiter-confirmation setting, so it is a station screen for
  // the same reason the till and the pass are: opened at the start of a
  // shift and watched under the provider's own SLA clock, not a dialog
  // reachable from Orders. `delivery_aggregators.view` matches the route.
  {
    to: '/delivery-aggregators',
    key: 'deliveryAggregators.nav',
    icon: ShoppingBag,
    perms: ['delivery_aggregators.view'],
  },
  { to: '/kitchen', key: 'nav.kitchen', icon: ChefHat, perms: ['kitchen.view'] },
  // The pass is the kitchen's other end, so it sits next to it rather than with
  // the call queue it also carries. `orders.view`, the same key as its route:
  // the pass is the order list grouped by table.
  { to: '/waiter-station', key: 'nav.waiterStation', icon: ConciergeBell, perms: ['orders.view'] },
  // §8 / §17 — the till, and a station screen for the same reason the pass is
  // one: it is opened at the start of a shift and stood at until close. Gated on
  // `orders.create`, the key that makes it useful and the one a CASHIER holds —
  // and the same key as its route, so this row never leads to a 403. `calculator`
  // because no other row is a keypad; `shopping-cart` is already Procurement's.
  { to: '/till', key: 'nav.register', icon: Calculator, perms: ['orders.create'] },
  // §4 / ADR-0008 — the time clock, a station screen for the same reason the
  // till beside it is one: opened once at the start of a shift and left
  // running. `attendance.punch` is "clock MYSELF in and out" and every floor
  // role holds it — a waiter who cannot clock in is a waiter who is not paid.
  { to: '/time-clock', key: 'workforce.nav.timeClock', icon: Clock, perms: ['attendance.punch'] },
  { to: '/checks', key: 'nav.checks', icon: ReceiptText, perms: ['checks.view'] },
  // ADR-0021 §15 — the call-centre desk, a station screen for the same
  // reason the till above is one: an agent opens it at the start of a shift
  // and works calls from it until close. `orders.create` matches the
  // route's own gate — the key that makes the screen useful, and the one
  // `CALL_CENTER_AGENT` holds.
  { to: '/call-center', key: 'callCenter.nav', icon: Headset, perms: ['orders.create'] },
  // ADR-0012 — gift cards and store credit. `wallet` (lucide) is unused
  // elsewhere on the rail; `wallet.view` matches the route's own gate.
  { to: '/wallet', key: 'nav.wallet', icon: Wallet, perms: ['wallet.view'] },
  // §24.5 — a refund or discount waiting on a manager needs to be one click
  // from wherever they are, so it sits with the operational screens.
  { to: '/approvals', key: 'platform.nav.approvals', icon: ShieldCheck, perms: ['approvals.view'] },
  // §22 — the case queue a branch manager checks like the approval queue
  // beside it, which is why it sits next to it rather than off in Settings.
  // Either `risk.review` or `risk.configure` shows the row, matching the
  // route's own `canAny` gate — a tenant admin who holds only the configure
  // key still has somewhere to land, on Health or Rules rather than the
  // queue `risk.review` alone unlocks.
  { to: '/risk', key: 'risk.nav', icon: ShieldAlert, perms: ['risk.review', 'risk.configure'] },
  { to: '/service-requests', key: 'platform.nav.serviceRequests', icon: BellRing, perms: ['service_requests.view'] },
  // §16 / ADR-0029 — a dispatcher's board, left running all shift the same
  // way the till and the pass are, which is what earns it a row of its own
  // rather than living as a Settings tab. `canAny` over every tab's own read
  // key, matching the route gate and the Fleet/Risk/Workforce I-07 shape — a
  // DRIVER holding only `driver_shifts.view` still gets a door here, landing
  // on their own shift rather than a 403.
  {
    to: '/delivery',
    key: 'delivery.nav',
    icon: Bike,
    perms: ['deliveries.view', 'driver_shifts.view', 'drivers.view', 'delivery_zones.view', 'delivery_location.configure'],
  },
  { to: '/catalog', key: 'nav.catalog', icon: BookOpen, perms: ['menu.view'] },
  // §20 — draft, review, publish and roll back what a branch carries or
  // charges (ADR-0011). A row of its own rather than a Catalog tab: it is
  // gated on `menu_governance.view`, a key only tenant admins and owners hold
  // by default, and a branch manager who holds `menu.view` must never be
  // offered this door — `git-branch` for the draft→review→publish→rollback
  // shape, and unused by any row above.
  { to: '/menu-governance', key: 'menuGovernance.nav', icon: GitBranch, perms: ['menu_governance.view'] },
  // ADR-0023 §19 — a franchisor manages several branches under one brand:
  // standards, consolidated analytics, royalty and onboarding templates.
  // 18 backend routes with no dashboard reach before this — a row of its
  // own for the same reason Menu Governance earned one two rows up: this is
  // a screen someone runs the business from, repeatedly, not a dialog
  // reachable off an existing page. `brands.view` matches the route gate,
  // so a franchisee branch manager who holds only that key still gets in
  // and lands on Overview rather than a screen gated one level higher.
  { to: '/brands', key: 'brands.nav', icon: Building2, perms: ['brands.view'] },
  { to: '/inventory', key: 'nav.inventory', icon: Archive, perms: ['inventory.view'] },
  { to: '/procurement', key: 'platform.nav.procurement', icon: ShoppingCart, perms: ['inventory.view'] },
  { to: '/recipes', key: 'platform.nav.recipes', icon: CookingPot, perms: ['inventory.view'] },
  { to: '/reservations', key: 'nav.reservations', icon: Calendar, perms: ['reservations.view'] },
  { to: '/customers', key: 'nav.customers', icon: User, perms: ['customers.view'] },
  // §4 / ADR-0008 — staff records, timesheets, exceptions, the attendance
  // policy and scheduling (roster, templates, availability, swaps, labour,
  // and — `scheduling.view_own` — the "My schedule" tab every floor role
  // holds: a waiter's own shifts, their availability, asking for a swap).
  // `Users` (plural), distinct from Customers' own `User` above.
  {
    to: '/workforce',
    key: 'workforce.nav.workforce',
    icon: Users,
    perms: ['employees.view', 'attendance.view', 'scheduling.view', 'scheduling.view_own'],
  },
  // ADR-0016 §8 — branding, the custom domain, branch publishing, opening
  // hours, SEO and abandoned-cart telemetry. `Globe` is unused elsewhere and
  // is literally what a public storefront is; `storefront.manage_domains`
  // matches the route's own broader gate (see `Routes.tsx`).
  {
    to: '/storefront',
    key: 'storefront.nav',
    icon: Globe,
    perms: ['storefront.view', 'storefront.manage_domains'],
  },
  // ADR-0026 §9 — templates, campaigns, and the tenant-wide quiet-hours and
  // fatigue-cap controls. `MessageSquare` is unused elsewhere on the rail.
  {
    to: '/messaging',
    key: 'messaging.nav',
    icon: MessageSquare,
    perms: ['message_templates.view', 'campaigns.view'],
  },
  { to: '/reports', key: 'nav.reports', icon: ChartLine, perms: ['reports.view'] },
  { to: '/audit', key: 'platform.nav.audit', icon: History, perms: ['reports.view'] },
  // ADR-0032 — a typed question over the same governed numbers Reports
  // shows, not a list to open and leave running, so it earns a row of its
  // own rather than living as a Reports tab (contrast forecasting, which
  // IS one — see `tabRoutes.ts`'s `REPORTS_TABS`). `copilot.use` matches
  // the route gate exactly, so nobody sees a door that answers 403 — and
  // because it defaults to TENANT_OWNER/TENANT_ADMIN only, most roles will
  // never see this row at all, which is the intended staged rollout.
  { to: '/copilot', key: 'nav.copilot', icon: Sparkles, perms: ['copilot.use'] },
  // §11 / ADR-0009 — the device and printer estate. A row of its own rather
  // than a Settings tab, because its first tab is the print spool: the screen an
  // operator opens at 8pm on a Friday when tickets stop coming out, which is
  // operations, not administration. Any one of the four read keys shows the row,
  // matching the route gate, so a shift manager holding only `print_jobs.view`
  // still gets in and lands on the one tab they hold. `printer` because no other
  // row is hardware.
  {
    to: '/fleet',
    key: 'fleet.title',
    icon: Printer,
    perms: ['print_jobs.view', 'devices.view', 'printers.view', 'print_bridges.view'],
  },
  // §2 / §3 — the provider catalogue, a venue's installations, and its
  // outbound webhook endpoints, ADR-0002 and ADR-0004. A row of its own
  // rather than a Settings tab: this is where an integration engineer looks
  // when a delivery is failing, which is operations, not administration —
  // the same reasoning that gives Fleet its own row. Either read key shows
  // it, matching the route gate, so a person who holds only `webhooks.view`
  // still gets in and lands on the one tab they hold.
  {
    to: '/integrations',
    key: 'integrations.nav',
    icon: Webhook,
    perms: ['integrations.view', 'webhooks.view'],
  },
  // §5 / ADR-0005 — the reconciliation screen a compliance operator opens
  // every day, so it earns a row rather than living under Settings: a
  // rejected fiscal document is a TASK, not a configuration setting, and the
  // screen that shows it is operations. `landmark` because no other row
  // reports to a government authority. `fiscal.view` matches the route.
  { to: '/fiscal', key: 'fiscal.title', icon: Landmark, perms: ['fiscal.view'] },
  // ADR-0028 Part A — the accounting posting ledger: did a sale reach the
  // venue's connected ledger, why not if it didn't, and reversing one that
  // was wrong. A row of its own rather than a tab under Integrations, for
  // the same reason Fiscal already sits beside Integrations rather than
  // inside it even though both fan out through the same substrate: the
  // audience is different. `integrations.view`/`webhooks.view` is an
  // integration engineer's key ("where a delivery is failing" — nav.ts's own
  // comment on that row); `accounting.view` is a bookkeeper's, and a
  // bookkeeper reconciling postings does not need to see, and should not be
  // handed, every webhook endpoint in the venue. `scale` because no other
  // row is a ledger.
  { to: '/accounting', key: 'accounting.nav', icon: Scale, perms: ['accounting.view'] },
  // §18 / ADR-0015 — the commissary. A row of its own for the same reason
  // Fleet has one: `in-transit` is a state someone has to watch, and the
  // screen an operator opens when a van has not arrived is operations, not
  // administration. `truck` is unused elsewhere and is literally the thing a
  // transfer moves stock on. Any one of the four read keys shows the row,
  // matching the route gate.
  {
    to: '/central-kitchen',
    key: 'centralKitchen.title',
    icon: Truck,
    perms: ['stock_transfers.view', 'production.view', 'requisitions.view', 'replenishment.view'],
  },
  // C-15 — sits beside Settings because that is where most of its links land,
  // and only for the roles that can actually close a gap (same keys as the route).
  { to: '/setup', key: 'setup.wizard.title', icon: ListChecks, perms: SETUP_FIX_PERMISSIONS },
  // I-07 — matches the route gate: the Settings shell renders whichever tabs
  // the actor holds, so an RBAC admin with only `users.view` still belongs
  // here. `booking_widget.view` (§13 / ADR-0018) and ADR-0027's
  // `marketplace.browse` / `marketplace.install` join the set for the same
  // reason: a host-desk lead who only manages the booking page, or a tenant
  // that only installs someone else's app, must still see this row rather
  // than have it disappear entirely.
  {
    to: '/settings',
    key: 'nav.settings',
    icon: SettingsIcon,
    perms: [
      'settings.view',
      'users.view',
      'roles.view',
      'devices.view',
      'booking_widget.view',
      'marketplace.browse',
      'marketplace.install',
    ],
  },
];

/**
 * The rows one role may actually see.
 *
 * Lives here rather than in `Layout` because it is half of a rule whose other
 * half is in `Routes.tsx`: a row is shown on the same key its route is gated
 * on, so nobody is offered a door that answers 403. Keeping the filter beside
 * the table is what lets `nav.test.ts` hold both halves still.
 *
 * Superseded in the rail itself by {@link visibleNavSections} below, which
 * groups the same filtered rows; kept because `Routes.test.tsx` still walks
 * the flat `NAV` table to prove every row leads somewhere reachable, and a
 * flat "everything this role can see" list is the right shape for that.
 *
 * @param canAny `usePermission().canAny` — any one of an entry's keys is enough,
 * matching `PermissionRoute`, which is also `canAny`.
 */
export function visibleNav(canAny: (...keys: string[]) => boolean) {
  return NAV.filter((item) => !item.perms.length || canAny(...item.perms));
}

export interface NavSection {
  /** The *section's* own i18n key — distinct from any row's `key` inside it. */
  key: string;
  items: NavEntry[];
}

/**
 * The rail, grouped by what someone is doing rather than which backend module
 * owns the screen — Floor map, Waiter station and Kitchen are all "someone
 * standing up during service"; Reports and Copilot are both "someone sitting
 * down asking questions" — and ordered by how often that doing happens, not
 * alphabetically and not by importance in the abstract.
 *
 * Overview is deliberately in no section: it is the one row with no
 * `perms` at all, the front door everyone lands on, and grouping it with
 * anything else would be grouping by nothing.
 *
 *  1. **Service** — the floor and the stations behind it, opened continuously
 *     by whoever is on shift: the host desk, the pass, the kitchen, the till,
 *     the clock. The highest-frequency doing in the app.
 *  2. **Channels** — orders arriving from outside the room and the driver
 *     board that fulfils them, watched under somebody else's SLA for the same
 *     reason a till is stood at: opened at the start of a shift, left
 *     running, but by a narrower set of roles than Service.
 *  3. **Guests & menu** — what is on offer and who is buying it: catalogue,
 *     reservations, customers, and the governance/franchise/storefront/
 *     messaging screens that shape those from further back. Checked many
 *     times a day, not stood at.
 *  4. **Management** — a shift or branch manager's own recurring checks:
 *     the approval queue, the risk queue, the roster. Several times a day,
 *     not continuously.
 *  5. **Supply** — stock, purchasing, recipes and the commissary: checked
 *     daily or every few days, not during service itself.
 *  6. **Insights** — sitting down and asking a question of the numbers:
 *     reports, the audit trail, the copilot. Daily-to-weekly.
 *  7. **Finance** — reconciling postings and fiscal documents. Daily for the
 *     narrow bookkeeper/compliance audience who own it, which is why it
 *     ranks below Insights despite the cadence.
 *  8. **Configuration** — configuring the venue itself: fleet, integrations,
 *     setup, settings. Done rarely, by design the last section on the rail.
 *
 * Collapsed to 64px there is no room for any of the labels above, so each
 * section also draws a `border-t` divider before its first icon (see
 * `Layout.tsx`) — the 37 icons read as eight visually chunked clusters
 * instead of one undifferentiated column — and the whole group carries a
 * native `title` plus an `aria-label` so the section's name is still
 * reachable on hover and to a screen reader with no text on screen at all.
 */
const NAV_SECTION_ROUTES: { key: string; routes: string[] }[] = [
  {
    key: 'nav.sections.service',
    routes: [
      '/floor-map',
      '/kids',
      '/reservations',
      '/orders',
      '/kitchen',
      '/waiter-station',
      '/till',
      '/checks',
      '/wallet',
      '/time-clock',
      '/service-requests',
    ],
  },
  {
    key: 'nav.sections.channels',
    routes: ['/call-center', '/delivery-aggregators', '/delivery'],
  },
  {
    key: 'nav.sections.guests',
    routes: ['/catalog', '/customers', '/menu-governance', '/brands', '/storefront', '/messaging'],
  },
  {
    key: 'nav.sections.management',
    routes: ['/approvals', '/risk', '/workforce'],
  },
  {
    key: 'nav.sections.supply',
    routes: ['/inventory', '/procurement', '/recipes', '/central-kitchen'],
  },
  {
    key: 'nav.sections.insights',
    routes: ['/reports', '/audit', '/copilot'],
  },
  {
    key: 'nav.sections.finance',
    routes: ['/fiscal', '/accounting'],
  },
  {
    key: 'nav.sections.configuration',
    routes: ['/fleet', '/integrations', '/setup', '/settings'],
  },
];

export const NAV_SECTIONS: NavSection[] = NAV_SECTION_ROUTES.map(({ key, routes }) => ({
  key,
  items: routes.map((to) => {
    const item = NAV.find((entry) => entry.to === to);
    if (!item) throw new Error(`nav section "${key}" references an unknown route "${to}"`);
    return item;
  }),
}));

/**
 * The rail's sections, each filtered to the rows this role may see and
 * dropped entirely once that leaves it with none.
 *
 * A section that renders empty is worse than no section — it draws a divider
 * and a header over nothing, and for a cashier who can see only Till and
 * Checks it would advertise a Finance or Configuration group they have no
 * door into. Filter-then-drop is what stops that: a section only reaches
 * `Layout` once it has at least one row this role can open.
 *
 * @param canAny `usePermission().canAny`, exactly as {@link visibleNav} takes it.
 */
export function visibleNavSections(canAny: (...keys: string[]) => boolean): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    key: section.key,
    items: section.items.filter((item) => !item.perms.length || canAny(...item.perms)),
  })).filter((section) => section.items.length > 0);
}
